/**
 * sieges system — v0M4.2.2 implementation (replaces the v0M2.3 stub).
 *
 * Per BATTLES.md §3.1 and §3.4. Each tick, for every Settlement in state:
 *
 *   1. If the city has an active siege, evaluate break conditions:
 *      a. Sieging army destroyed / disbanded → clear.
 *      b. Sieging army is currently in a battle → clear (§3.1 break
 *         condition b: field army contacted sieger; siege reopens fresh
 *         if sieger wins the field battle, per PLAYTEST-OPEN in §3.10).
 *      c. Sieging army no longer adjacent to the city cell → clear.
 *      d. Otherwise: decrement `daysRemaining` by 1 / TICKS_PER_GAME_DAY.
 *      e. If `daysRemaining ≤ 0`: capture the city per §3.8.
 *
 *   2. If the city has no active siege, look for a hostile adjacent
 *      army with no defending field army in the same province:
 *      a. Adjacent = Chebyshev distance ≤ SIEGE_ADJACENCY_RADIUS (1.5)
 *         from the city cell (allows float-position armies on any of
 *         the 8 surrounding cells with slop).
 *      b. Hostile = the army's owning faction is on a different team
 *         than the city's owning faction. Same-team armies are ignored.
 *      c. No-defender = no army of the city-owner team currently sits
 *         inside the same province.
 *      d. Multiple-attackers tie-break: lowest ArmyId wins (deterministic
 *         per SG iteration rule).
 *      e. Skip if sieger is in a battle (can't hold a siege while
 *         actively fighting a field battle).
 *
 * Runs after `battles` and before `economy` in the fixed system order
 * (`systems.ts`) — sees this tick's battle outcomes (so a sieger who
 * won a field battle this tick already has `currentBattleId === null`)
 * and its state updates gate `economy.ts` production for the current
 * tick.
 *
 * Capture semantics (§3.8):
 *   - Province ownership flips to the sieger's faction.
 *   - Settlement population −1.
 *   - Recruit queue cleared, build queue cleared.
 *   - All garrison cohorts destroyed (they defended and lost).
 *   - Siege state cleared.
 *
 * Cross-refs:
 *   - Resource denial while under siege: `economy.ts` reads
 *     `city.siegeState` and zeros production paths for the province.
 *   - Assault path (§3.5) — deferred to v0M4.2.5. This system only
 *     implements the starve-out path.
 */

import type { Command } from './commands';
import type { Army, Settlement, GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { sortedIds } from './state';
import { destroyAllCohortsOfArmy, disbandArmy } from './battle-conclusion';
import { getDialNumber, loadDials } from '../data/dials';

// ---------------------------------------------------------------------------
// Dials — loaded once at module init from Balance.xlsx.
// ---------------------------------------------------------------------------

const SIEGE_DIALS = loadDials();
const SIEGE_SURRENDER_DAYS = getDialNumber(SIEGE_DIALS, 'siege_surrender_days');
// MFix5.1 (2026-07-22): city ZoC radius (cells, Chebyshev). Enemy army
// entering this radius around a hostile city auto-opens a siege. Add a
// +0.5 slop to absorb float army positions on the boundary cells,
// matching the pattern in the old SIEGE_ADJACENCY_RADIUS = 1.5 constant.
const SETTLEMENT_ZOC_RADIUS = getDialNumber(SIEGE_DIALS, 'settlement_zoc_radius');
// v0M7.4d+siege2 (2026-07-26) — Fort surrender timer. Peer of
// `siege_surrender_days` scoped to Forts so Michael can tune the two
// independently (Forts have no population/starvation, so the curve is
// a pure design lever). Fallback = 20 if the xlsx dial isn't authored
// yet — matches the apply-commands FORT_SURRENDER_DAYS default.
function safeDialNumber(name: string, fallback: number): number {
  try { return getDialNumber(SIEGE_DIALS, name); } catch { return fallback; }
}
const FORT_SURRENDER_DAYS = safeDialNumber('fort_surrender_days', 20);

// MFix3.8: canonical TICKS_PER_GAME_DAY lives in state.ts. Countdown
// converts per-tick decrement to game-day fraction so day-counters stay
// honest regardless of speed setting (sim-driver.ts owns the wall-clock
// ratio).
import { TICKS_PER_GAME_DAY } from './state';
const DAYS_PER_TICK = 1 / TICKS_PER_GAME_DAY;

/**
 * MFix5.1 (2026-07-22): threshold for "army is inside the city's ZoC".
 * Dial-driven via `settlement_zoc_radius` + 0.5 slop to absorb float
 * army positions on the boundary.
 *
 * MFix14 (2026-07-29): switched from Chebyshev (square) to Euclidean
 * (circle) per Michael's ask that settlement ZoCs match army ZoCs in
 * shape. Same threshold value, different distance metric. Also matches
 * `movement.ts::clampStepAgainstEnemySettlements` (halt distance =
 * `settlement_zoc_radius`, trigger radius = `settlement_zoc_radius +
 * 0.5`), so an enemy army halting at the movement clamp fires the
 * siege trigger on the same tick.
 */
const SIEGE_ADJACENCY_RADIUS = SETTLEMENT_ZOC_RADIUS + 0.5;

// ---------------------------------------------------------------------------
// System entry point
// ---------------------------------------------------------------------------

export function siegesSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  // Snapshot the city ids up front — capture (§3.8) may not delete cities
  // at v1 (the city itself persists, only the province owner flips), but
  // the pattern is safer for future amendments where a captured minor
  // city is disabled or destroyed.
  const cityIds = sortedIds(state.settlements);

  for (const settlementId of cityIds) {
    const city = state.settlements.get(settlementId);
    if (!city) continue;
    const province = state.provinces.get(city.provinceId);
    if (!province) continue;
    // v0M9 (2026-07-27) — skip minor settlements. Minors don't get
    // sieged / captured; they get pillaged per MECHANICS.md §2 v0M9
    // amendment. The pillage timer lives in `pillages.ts`.
    if (city.isMinor) continue;
    // MFix3.2: neutral cities (province.ownerFactionId === null) can now be
    // sieged. All armies count as hostile to a neutral city; no defender-
    // team check applies. Capture flips ownership to the sieger's faction.

    if (city.siegeState !== null) {
      updateActiveSiege(state, city, context);
    } else {
      maybeOpenSiege(state, city, context);
    }
  }

  // v0M7.4d+siege2 (2026-07-26) — Fort-siege pass, parity with the
  // settlement loop above. Only Forts (WorldStructures of kind='fort')
  // participate; other kinds ignored. Trigger + break conditions
  // mirror settlements; surrender writeback matches assault outcome
  // (owner flip + garrison destroyed) per session-33 elicited scope.
  if (state.worldStructures) {
    const fortIds = sortedIds(state.worldStructures);
    for (const wsId of fortIds) {
      const fort = state.worldStructures.get(wsId);
      if (!fort) continue;
      if (fort.kind !== 'fort') continue;
      if (fort.ownerFactionId === null) continue; // Neutral Forts don't get sieged at v1.

      if (fort.siegeState != null) {
        updateActiveFortSiege(state, fort);
      } else {
        maybeOpenFortSiege(state, fort);
      }
    }
  }

  return state;
}

// ---------------------------------------------------------------------------
// Active-siege update — break conditions + countdown + capture
// ---------------------------------------------------------------------------

function updateActiveSiege(state: GameState, city: Settlement, context: SimContext): void {
  const siege = city.siegeState;
  if (siege === null) return; // Defensive; caller already checked.

  const sieger = state.armies.get(siege.siegingArmyId);
  // Break (a): sieger destroyed.
  if (!sieger) {
    city.siegeState = null;
    return;
  }
  // Break (b): sieger in a battle (field battle interrupted the siege).
  // Per §3.10 PLAYTEST-OPEN, the timer resets rather than pausing —
  // clearing here + letting `maybeOpenSiege` reopen on the next
  // no-battle tick achieves the reset semantics.
  if (sieger.currentBattleId !== null) {
    city.siegeState = null;
    return;
  }
  // Break (c): sieger no longer adjacent to the city cell.
  if (!isArmyAdjacentToCity(sieger, city)) {
    city.siegeState = null;
    return;
  }

  // Countdown. daysRemaining decrements by DAYS_PER_TICK per tick.
  siege.daysRemaining -= DAYS_PER_TICK;
  if (siege.daysRemaining <= 0) {
    // (d) → (e) Capture per §3.8.
    captureCity(state, city, sieger, context);
  }
}

// ---------------------------------------------------------------------------
// Trigger evaluation — open a siege if conditions hold
// ---------------------------------------------------------------------------

function maybeOpenSiege(state: GameState, city: Settlement, context: SimContext): void {
  const province = state.provinces.get(city.provinceId);
  if (!province) return;
  const ownerFactionId = province.ownerFactionId;
  // MFix3.2: neutral cities (ownerFactionId === null) allow siege by any
  // army. ownerFaction is null in that case; hostility + defender-in-
  // province checks below handle the null case explicitly.
  let ownerFaction: import('./state').Faction | null = null;
  if (ownerFactionId !== null) {
    const looked = state.factions.get(ownerFactionId);
    if (!looked) return; // Owned by an unknown faction — malformed state.
    ownerFaction = looked;
  }

  // No-defender-field-army check per §3.1: if any army of the city-owner
  // team currently sits inside the same province, we do NOT open a
  // siege — the sieger must be cleared by a field battle first (which
  // zoc-detection handles independently). MFix3.2: neutral cities have
  // no owner team, so this check is skipped entirely (no defender exists).
  if (ownerFaction !== null && defenderFieldArmyInProvince(state, context, ownerFactionId!, city.provinceId, ownerFaction.teamId)) {
    return;
  }

  // Find the lowest-id hostile army adjacent to the city cell that is
  // not itself in a battle. Same-team armies are skipped. MFix3.2: for
  // neutral cities (ownerFaction === null), every army is hostile —
  // any adjacent army qualifies as sieger.
  let bestSieger: Army | null = null;
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    if (army.currentBattleId !== null) continue;
    const armyFaction = state.factions.get(army.ownerFactionId);
    if (!armyFaction) continue;
    if (ownerFaction !== null && armyFaction.teamId === ownerFaction.teamId) continue; // Not hostile.
    if (!isArmyAdjacentToCity(army, city)) continue;

    bestSieger = army;
    break; // sortedIds guarantees ascending; first match is the lowest-id winner.
  }
  if (bestSieger === null) return;

  // MFix5.2 (2026-07-22): if the target city has NO garrison AND (per
  // the earlier check) no defender field army in the province, the city
  // is completely undefended — capture immediately (owner flip) instead
  // of opening a siege timer. Matches Michael's playtest ask: "If the
  // city does not have a garrison (or a nearby army) the city should
  // surrender (owner flip) immediately. No siege required."
  //
  // The "nearby army" leg is already satisfied by the earlier
  // defenderFieldArmyInProvince check that returned without opening a
  // siege (line ~176) — reaching this point means no defender field
  // army is in the province. So the only remaining check is: does the
  // city itself have any garrison cohorts?
  if (city.garrisonCohortIds.length === 0) {
    // MFix10 (2026-07-24) — halt the sieger before capturing so a march
    // order past the city doesn't carry the army out of the province the
    // instant it flips owner. Matches the field-battle lock pattern in
    // `battle-factory.ts` (currentBattleId set + path cleared atomically).
    bestSieger.plannedPath = null;
    bestSieger.movementGoal = null;
    captureCity(state, city, bestSieger, context);
    return;
  }

  city.siegeState = {
    siegingArmyId: bestSieger.id,
    siegeSinceTick: state.tick,
    daysRemaining: SIEGE_SURRENDER_DAYS,
  };
  // MFix10 (2026-07-24) — halt the sieger the tick the siege opens.
  // Without this, an army whose plannedPath extends past the city walks
  // straight through, exceeds SIEGE_ADJACENCY_RADIUS on a subsequent
  // tick, and break-condition (c) clears the siege it just opened —
  // symptom Michael reported as "army keeps moving after the battle
  // icon appears". Battles already halt both armies via
  // `battle-factory.ts` (currentBattleId set + path cleared); sieges
  // are the only lock class that was missing this.
  bestSieger.plannedPath = null;
  bestSieger.movementGoal = null;
}

// ---------------------------------------------------------------------------
// Capture — flip ownership + wipe defender-side state
// ---------------------------------------------------------------------------

/**
 * v0M4.2.5 — exported for `battle-conclusion.ts` to call on attacker
 * victory in a siege battle. See §3.8 for the capture semantics
 * (province flip / population -1 / garrison destroyed / queues cleared /
 * siegeState cleared).
 */
export function captureCity(state: GameState, city: Settlement, sieger: Army, _context: SimContext): void {
  const province = state.provinces.get(city.provinceId);
  if (!province) return;

  // v0M9-followup.3 (2026-07-27) — Minor settlements cannot be captured.
  // If a siege battle at a minor concluded with attacker victory (garrison
  // defeated in the battle), destroy any surviving garrison cohorts +
  // early-return. Next tick's `pillages.ts` sees an empty garrison + the
  // attacker still in ZoC and opens the pillage timer per the raider model.
  if (city.isMinor) {
    const garrisonIds = [...city.garrisonCohortIds];
    for (const gid of garrisonIds) {
      const gc = state.cohorts.get(gid);
      if (gc) {
        gc.aliveFigureCount = 0;
        state.cohorts.delete(gid);
      }
    }
    city.garrisonCohortIds = [];
    // Preserve province ownership, population, queues, and (absent)
    // siegeState. Sieger stays in place — pillage timer opens next tick.
    void sieger;
    return;
  }

  // Ownership flip. At v1 every non-minor Settlement is a major city, so
  // this always flips province ownership too.
  province.ownerFactionId = sieger.ownerFactionId;

  // Population −1 per existing city-capture behaviour (§3.8).
  province.population = Math.max(0, province.population - 1);

  // Clear recruit + build queues — new owner doesn't inherit progress.
  province.recruitQueue.length = 0;
  province.buildQueue.length = 0;

  // Destroy all garrison cohorts — they defended and lost. Snapshot the
  // id list because `destroyGarrison` mutates `city.garrisonCohortIds`.
  const garrisonIds = [...city.garrisonCohortIds];
  for (const gid of garrisonIds) {
    const gcohort = state.cohorts.get(gid);
    if (!gcohort) continue;
    const idx = city.garrisonCohortIds.indexOf(gid);
    if (idx >= 0) city.garrisonCohortIds.splice(idx, 1);
    state.cohorts.delete(gid);
  }

  // Clear the siege state — the siege has resolved by capture.
  city.siegeState = null;

  // Loser field-army handling: at v0M4.2.2 the "no defender field army"
  // trigger check meant no defender army was in the province to begin
  // with. So no field army needs to be destroyed here. (If a defender
  // field army had arrived mid-siege, zoc-detection would have created
  // a field battle and the siege would have cleared per break (b).)
  //
  // Sieger post-siege: leave the sieger army in place. Player can
  // reissue movement orders next tick. (Auto-garrison of the sieger
  // into the captured city is a candidate polish, deferred.)

  // v0M4.2.2: intentionally silent about the auto-pause "city captured"
  // event — that hook lives client-side per MECHANICS.md §speed
  // auto-pause events, and the client polls `province.ownerFactionId`
  // per-render to detect changes.

  // Silence unused-var warning until sieger is actually consumed by a
  // future extension (loot / sack UI stretch per §3.8).
  void sieger;
}

// ---------------------------------------------------------------------------
// Adjacency helper
// ---------------------------------------------------------------------------

/**
 * True iff `army` sits within `SIEGE_ADJACENCY_RADIUS` Euclidean cells
 * of the city's cell centre. MFix14 (2026-07-29): was Chebyshev
 * (square); now Euclidean (circle) to match the settlement ZoC visual
 * + the movement clamp in movement.ts.
 */
function isArmyAdjacentToCity(army: Army, city: Settlement): boolean {
  const dx = army.position.x - (city.cell.x + 0.5);
  const dy = army.position.y - (city.cell.y + 0.5);
  return Math.sqrt(dx * dx + dy * dy) <= SIEGE_ADJACENCY_RADIUS;
}

/**
 * True iff any army of the city-owner team currently sits inside
 * `provinceId` (per `context.provinceMap`). Test-fixture escape hatch:
 * if `provinceMap` is missing, returns false (no defender detected).
 */
function defenderFieldArmyInProvince(
  state: GameState,
  context: SimContext,
  _ownerFactionId: import('./state').FactionId,
  provinceId: import('./state').ProvinceId,
  ownerTeamId: number,
): boolean {
  const pmap = context.provinceMap;
  if (!pmap) return false;
  const w = context.terrain.width;
  const h = context.terrain.height;

  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    const armyFaction = state.factions.get(army.ownerFactionId);
    if (!armyFaction) continue;
    if (armyFaction.teamId !== ownerTeamId) continue; // Only same-team defenders count.

    const cx = Math.floor(army.position.x);
    const cy = Math.floor(army.position.y);
    if (cx < 0 || cx >= w || cy < 0 || cy >= h) continue;
    const mask = pmap[cy * w + cx];
    if (mask === undefined) continue;
    // The mask stores the ProvinceId as a number; brand-cast for comparison.
    if ((mask as unknown as number) === (provinceId as unknown as number)) {
      return true;
    }
  }
  return false;
}

// Re-export helpers not used elsewhere yet but that tests may want.
export const _siegeInternals = {
  SIEGE_ADJACENCY_RADIUS,
  DAYS_PER_TICK,
  SIEGE_SURRENDER_DAYS,
  FORT_SURRENDER_DAYS,
};

// Silence unused-import warnings until future extension pulls these in.
void destroyAllCohortsOfArmy;
void disbandArmy;

// ---------------------------------------------------------------------------
// v0M7.4d+siege2 (2026-07-26) — Fort-siege helpers
// ---------------------------------------------------------------------------

/**
 * True iff `army` sits within `SIEGE_ADJACENCY_RADIUS` Euclidean cells
 * of the Fort's cell centre. Peer of `isArmyAdjacentToCity`. MFix14
 * (2026-07-29): was Chebyshev; now Euclidean per the circular-ZoC lock.
 */
function isArmyAdjacentToFort(army: Army, fort: import('./state').WorldStructure): boolean {
  const dx = army.position.x - (fort.cell.x + 0.5);
  const dy = army.position.y - (fort.cell.y + 0.5);
  return Math.sqrt(dx * dx + dy * dy) <= SIEGE_ADJACENCY_RADIUS;
}

/**
 * True iff a friendly-to-Fort-owner Army sits ON the Fort cell itself
 * (Chebyshev-0). Peer of the province-based defender check for
 * settlements — Forts don't have a province model of their own, so
 * "defender present" collapses to "someone friendly is on the Fort".
 * Simpler + more spatially accurate for the point-structure model.
 */
function friendlyDefenderOnFort(
  state: GameState,
  fort: import('./state').WorldStructure,
): boolean {
  const ownerFactionId = fort.ownerFactionId;
  if (ownerFactionId === null) return false;
  const ownerFaction = state.factions.get(ownerFactionId);
  if (!ownerFaction) return false;
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    const armyFaction = state.factions.get(army.ownerFactionId);
    if (!armyFaction) continue;
    if (armyFaction.teamId !== ownerFaction.teamId) continue;
    const cx = Math.floor(army.position.x);
    const cy = Math.floor(army.position.y);
    if (cx === fort.cell.x && cy === fort.cell.y) return true;
  }
  return false;
}

function updateActiveFortSiege(state: GameState, fort: import('./state').WorldStructure): void {
  const siege = fort.siegeState;
  if (siege == null) return;

  const sieger = state.armies.get(siege.siegingArmyId);
  // Break (a): sieger destroyed.
  if (!sieger) {
    fort.siegeState = null;
    return;
  }
  // Break (b): sieger in a battle (field battle interrupted the siege).
  if (sieger.currentBattleId !== null) {
    fort.siegeState = null;
    return;
  }
  // Break (c): sieger no longer adjacent to the Fort cell.
  if (!isArmyAdjacentToFort(sieger, fort)) {
    fort.siegeState = null;
    return;
  }
  // Break (d): a friendly-to-Fort-owner Army has arrived on the Fort
  // cell (defender relieves the siege). Peer of the settlement
  // defender-field-army-in-province gate; simpler because Forts are
  // point-structures.
  if (friendlyDefenderOnFort(state, fort)) {
    fort.siegeState = null;
    return;
  }

  // Countdown. Surrender at 0.
  siege.daysRemaining -= DAYS_PER_TICK;
  if (siege.daysRemaining <= 0) {
    surrenderFort(state, fort, sieger);
  }
}

function maybeOpenFortSiege(state: GameState, fort: import('./state').WorldStructure): void {
  const ownerFactionId = fort.ownerFactionId;
  if (ownerFactionId === null) return;
  const ownerFaction = state.factions.get(ownerFactionId);
  if (!ownerFaction) return;

  // No-defender check: any friendly Army on the Fort cell blocks siege.
  if (friendlyDefenderOnFort(state, fort)) return;

  // Find the lowest-id hostile army adjacent to the Fort cell not in
  // a battle. Deterministic per SG iteration rule.
  let bestSieger: Army | null = null;
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    if (army.currentBattleId !== null) continue;
    const armyFaction = state.factions.get(army.ownerFactionId);
    if (!armyFaction) continue;
    if (armyFaction.teamId === ownerFaction.teamId) continue; // Not hostile.
    if (!isArmyAdjacentToFort(army, fort)) continue;
    bestSieger = army;
    break;
  }
  if (bestSieger === null) return;

  // MFix5.2-style undefended shortcut: if Fort garrison is empty AND
  // no friendly defender is on the cell (already checked), surrender
  // immediately. Matches the settlement "no garrison = instant flip"
  // pattern — makes empty Forts strategic liabilities rather than
  // forcing pointless day-counter waits.
  if ((fort.garrisonCohortIds ?? []).length === 0) {
    bestSieger.plannedPath = null;
    bestSieger.movementGoal = null;
    surrenderFort(state, fort, bestSieger);
    return;
  }

  fort.siegeState = {
    siegingArmyId: bestSieger.id,
    siegeSinceTick: state.tick,
    daysRemaining: FORT_SURRENDER_DAYS,
  };
  // Halt the sieger so it doesn't march past on the next tick and
  // trip break-condition (c). Same discipline as the settlement
  // maybeOpenSiege pattern.
  bestSieger.plannedPath = null;
  bestSieger.movementGoal = null;
}

/**
 * v0M7.4d+siege2 (2026-07-26) — Fort surrender writeback per the
 * session-33 elicited policy: owner flips to the sieger's faction +
 * all garrison cohorts destroyed (matches assault outcome). Siege
 * state cleared; sieger stays in place.
 */
function surrenderFort(
  state: GameState,
  fort: import('./state').WorldStructure,
  sieger: Army,
): void {
  fort.ownerFactionId = sieger.ownerFactionId;
  if (fort.garrisonCohortIds) {
    for (const cid of fort.garrisonCohortIds) {
      const cohort = state.cohorts.get(cid);
      if (cohort) {
        cohort.aliveFigureCount = 0;
        state.cohorts.delete(cid);
      }
    }
    fort.garrisonCohortIds = [];
  }
  fort.siegeState = null;
}
